import { Directive, HostListener, input } from '@angular/core';

/** Supported event types for stopPropagation directive */
export type StopPropagationEvent =
  | 'click'
  | 'mousedown'
  | 'pointerdown'
  | 'change'
  | 'keydown.enter'
  | 'keydown.space'
  | 'keydown'
  | 'dblclick'
  | 'dragover'
  | 'drop';

/**
 * Directive to stop event propagation on specified events.
 *
 * Usage:
 *
 * ```html
 * <!-- Stops all supported events (default) -->
 * <button stopPropagation (click)="onEdit(row)">Edit</button>
 *
 * <!-- Stops only specified events -->
 * <button [stopPropagation]="['click']" (click)="onEdit(row)">Edit</button>
 * <button [stopPropagation]="['click', 'keydown.enter']" (click)="onEdit(row)">Edit</button>
 * ```
 */
@Directive({
  selector: '[stopPropagation]',
  standalone: true,
})
export class StopPropagationDirective {
  stopPropagation = input<StopPropagationEvent[]>([]);

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('click')) event.stopPropagation();
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('mousedown')) event.stopPropagation();
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('pointerdown')) event.stopPropagation();
  }

  @HostListener('change', ['$event'])
  onChange(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('change')) event.stopPropagation();
  }

  @HostListener('keydown.enter', ['$event'])
  onKeydownEnter(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('keydown.enter')) event.stopPropagation();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const events = this.stopPropagation();
    if (events.includes('keydown')) event.stopPropagation();
  }

  @HostListener('keydown.space', ['$event'])
  onKeydownSpace(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('keydown.space')) event.stopPropagation();
  }

  @HostListener('dblclick', ['$event'])
  onDblClick(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('dblclick')) event.stopPropagation();
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('dragover')) event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: Event): void {
    const events = this.stopPropagation();
    if (events.includes('drop')) event.stopPropagation();
  }
}
